/**
 * Reading and writing the Formily JSON schema a form is stored as.
 *
 * The one rule this file exists to enforce: **never lose what we did not
 * understand**. Every node keeps its original object in `raw`, and serialising
 * merges the handful of edited fields back over it. A form built in the old
 * antd 4 designer can therefore be opened here, have one label changed, and be
 * saved without quietly dropping the validators, decorator props or custom
 * component settings this designer has no editor for yet.
 *
 * That is what makes it safe to run the two designers side by side while this
 * one grows.
 */

import {
  applyRules,
  EMPTY_RULES,
  foreignReactions,
  foreignValidator,
  readRules,
  type Rules,
} from "./rules";
import { summarise } from "../markdown/markdown";

export type FormMeta = Record<string, any>;

export type Node = {
  /** `x-designable-id`, kept so the old designer still recognises the node. */
  id: string;
  /** The node's key inside its parent's `properties`. For a field this is also
   *  the name request data is stored against, so it is never rewritten. */
  key: string;
  component: string;
  /** "void" for layout containers, a JSON type for anything that holds a value. */
  type: string;
  title: string;
  required: boolean;
  componentProps: Record<string, any>;
  enumOptions: Array<{ label: string; value: string }> | null;
  /** Validation and conditional visibility, in the editable form. See rules.ts
   *  for why they are stored structured and generated into Formily's shape. */
  rules: Rules;
  /** This field carries a validator or a reaction written somewhere else — the
   *  classic antd-4 designer, or by hand. The inspector shows it read-only and
   *  serialising leaves it exactly as found; overwriting it would be the
   *  failure this whole file exists to prevent. */
  foreignRules: boolean;
  children: Node[];
  /**
   * Children stored under `items.properties` rather than `properties`.
   *
   * Only `ArrayTable` has them, and it has BOTH: its columns live in
   * `items.properties` while its "Add row" button is an ordinary child. Reading
   * only `properties` — which is what this file did — meant a repeating table
   * opened here showing one button and none of its columns, and there was no
   * way to edit one without going back to the antd 4 designer.
   */
  itemChildren: Node[];
  /**
   * What the field starts as, structured (C10). `null` for most fields.
   *
   * Stored as `x-designer-default` beside the plain Formily `default`, for the
   * same reason `x-designer-rules` sits beside `x-validator`: a generated
   * `default` cannot be read back to say *why* it is that value, and "the
   * person's own department" is not a value at all until somebody opens the
   * form.
   */
  defaultValue: FieldDefault | null;
  /** The untouched original, so unknown settings survive a round trip. */
  raw: Record<string, any>;
};

/**
 * Where a field's starting value comes from.
 *
 * `profile` is the whole point of the feature: every requester retypes their
 * own department and site onto every case, and those are two facts the system
 * already knows about them (A2). A fixed default is the ordinary half.
 */
export type FieldDefault =
  | { source: "fixed"; value: any }
  | { source: "profile"; key: ProfileKey }
  | { source: "today" };

export type ProfileKey =
  | "name"
  | "email"
  | "department"
  | "site"
  | "job_title"
  | "company"
  | "employee_id";

export const PROFILE_KEYS: Array<{ key: ProfileKey; label: string }> = [
  { key: "name", label: "Their full name" },
  { key: "email", label: "Their email" },
  { key: "department", label: "Their department" },
  { key: "site", label: "Their site" },
  { key: "job_title", label: "Their job title" },
  { key: "company", label: "Their company" },
  { key: "employee_id", label: "Their employee number" },
];

export type Doc = {
  form: FormMeta;
  /**
   * The root schema object as stored, minus its `properties`.
   *
   * It carries keys of its own — `x-designable-id` among them — and rebuilding
   * a fresh `{ type, properties }` root threw them away. `null` when the form
   * has no schema at all, which some section types legitimately do not.
   */
  schemaRaw: Record<string, any> | null;
  root: Node[];
};

/** Components that hold other components rather than a value. */
export const CONTAINERS = new Set([
  "FormLayout",
  "FormGrid",
  "FormGrid.GridColumn",
  "Card",
  "ArrayTable",
  "ArrayTable.Column",
  "FormItem",
]);

export const isContainer = (node: Node) =>
  CONTAINERS.has(node.component) || node.type === "void";

/** A repeating table. Its columns are `itemChildren`, not `children`. */
export const isTable = (node: Node) => node.component === "ArrayTable";

/** One column of a repeating table. Holds exactly one field. */
export const isColumn = (node: Node) => node.component === "ArrayTable.Column";

/**
 * Furniture: real nodes that are not the administrator's to arrange.
 *
 * `ArrayTable.Addition` is the table's own "Add row" button. It is a child like
 * any other in the schema, and showing it in the canvas beside the columns
 * invites somebody to drag or delete it, after which the table cannot have rows
 * added to it and nothing on screen explains why.
 */
export const isFurniture = (node: Node) =>
  node.component === "ArrayTable.Addition";

/**
 * What to call a container on screen.
 *
 * A Card's heading is `x-component-props.title`, not the schema-level `title` —
 * they are different keys and only the first one renders. Reading `node.title`
 * for a card showed every card as "Card".
 */
export const containerLabel = (node: Node): string => {
  if (node.component === "Card") return node.componentProps.title || "";
  if (node.component === "ArrayTable.Column") return node.componentProps.title || "";
  if (node.component === "Text") return node.componentProps.content || "";
  // A guidance block is named by its own first line, flattened: a card in the
  // canvas labelled `**Before you start**` shows the administrator the markers
  // rather than the words, which is the one place they are noise.
  if (node.component === "Markdown")
    return summarise(node.componentProps.content || "", 60);
  return node.title || "";
};

/** A component whose options the inspector can edit. */
export const HAS_OPTIONS = new Set([
  "Select",
  "Radio.Group",
  "Checkbox.Group",
]);

export type WidgetDef = {
  /**
   * What the palette button is, when that is not simply the component: two
   * entries can share one component with different defaults ("File" and
   * "Photo" are both `Upload`). Defaults to `component`.
   */
  id?: string;
  component: string;
  label: string;
  /** JSON type written into the schema for a new field of this kind. */
  type: string;
  /** Extra defaults merged into a freshly dropped field. */
  extra?: Record<string, any>;
};

/**
 * The palette.
 *
 * Deliberately only the components the existing 35 forms actually use — a
 * designer offering widgets nobody has ever placed is harder to scan, not more
 * capable.
 */
export const WIDGETS: WidgetDef[] = [
  { component: "Input", label: "Text", type: "string" },
  { component: "Input.TextArea", label: "Long text", type: "string" },
  { component: "NumberPicker", label: "Number", type: "number" },
  { component: "Select", label: "Dropdown", type: "string" },
  { component: "Radio.Group", label: "Choice", type: "string" },
  { component: "Checkbox.Group", label: "Multiple choice", type: "array" },
  { component: "DatePicker", label: "Date", type: "string" },
  { component: "Switch", label: "Yes / no", type: "boolean" },
  { component: "Rate", label: "Rating", type: "number" },
  { component: "Upload", label: "File", type: "array" },
  // Same component, pre-set to images: the difference an administrator is
  // choosing between is "attach a document" and "photograph something", and
  // `accept` is what makes the phone's picker offer the camera roll rather
  // than a file browser.
  {
    id: "Photo",
    component: "Upload",
    label: "Photo",
    type: "array",
    extra: { "x-component-props": { accept: "image/*", listType: "picture" } },
  },
  { component: "UserSelect", label: "Person", type: "string" },
  // Picks from a maintained list (C4) -- an asset, a vendor, a cost centre.
  // `entityType` is blank until the inspector sets it, and a lookup field with
  // no list chosen renders as a disabled box saying so rather than an empty
  // dropdown nobody can explain.
  {
    component: "EntitySelect",
    label: "From a list",
    type: "string",
    extra: { "x-component-props": { entityType: "" } },
  },
  // Formatted prose (C12) -- a method statement, a scope of work, findings.
  // Stored as markdown in the same text column as every other answer, so it
  // stays readable in a report and printable in the permit; see
  // `main/richtext.py` for why that is not HTML.
  { component: "RichText", label: "Formatted text", type: "string" },
];

/** What the palette calls this widget — its `id`, or the component itself. */
export const widgetId = (widget: WidgetDef): string =>
  widget.id ?? widget.component;

export const widgetLabel = (component: string): string =>
  WIDGETS.find((w) => w.component === component)?.label ?? component;

/** Formily's own id format, so nodes made here look native to the old designer. */
export const newId = (): string =>
  Math.random().toString(36).slice(2, 8) + Math.random().toString(36).slice(2, 7);

/* ------------------------------------------------------------------ parse */

const ordered = (properties: Record<string, any>): Node[] =>
  Object.entries(properties ?? {})
    // `x-index` is the stored order; fall back to key order when it is absent.
    .sort((a, b) => (a[1]?.["x-index"] ?? 0) - (b[1]?.["x-index"] ?? 0))
    .map(([childKey, child]) => toNode(childKey, child));

const toNode = (key: string, raw: Record<string, any>): Node => {
  const children = ordered((raw.properties ?? {}) as Record<string, any>);
  // A repeating table keeps its columns one level down, under `items`. Reading
  // only `properties` is why a table opened here used to show its "Add row"
  // button and nothing else.
  const itemChildren = ordered(
    (raw.items?.properties ?? {}) as Record<string, any>
  );

  const rawEnum = Array.isArray(raw.enum) ? raw.enum : null;

  return {
    id: raw["x-designable-id"] ?? newId(),
    key,
    component: raw["x-component"] ?? "",
    type: raw.type ?? "string",
    title: raw.title ?? "",
    required: raw.required === true,
    componentProps: { ...(raw["x-component-props"] ?? {}) },
    enumOptions: rawEnum
      ? rawEnum.map((o: any) => ({
          label: String(o?.label ?? o),
          value: String(o?.value ?? o),
        }))
      : null,
    rules: readRules(raw),
    foreignRules: foreignValidator(raw) || foreignReactions(raw),
    children,
    itemChildren,
    defaultValue: readDefault(raw),
    raw,
  };
};

/**
 * The structured default, or one inferred from a plain stored `default`.
 *
 * A form built in the classic designer can carry a `default` with no structured
 * copy beside it. Reporting that as "no default" would make the inspector show
 * an empty box over a field that visibly starts with a value, and saving would
 * then wipe it — the same class of mistake `foreignRules` exists to prevent,
 * except that a fixed default IS readable back, so it is adopted rather than
 * locked.
 */
const readDefault = (raw: Record<string, any>): FieldDefault | null => {
  const structured = raw["x-designer-default"];
  if (structured && typeof structured === "object" && structured.source) {
    return structured as FieldDefault;
  }
  if (raw.default !== undefined && raw.default !== null && raw.default !== "") {
    return { source: "fixed", value: raw.default };
  }
  return null;
};

export const parse = (schemaJson: any): Doc => {
  const schema = schemaJson?.schema;
  const properties = (schema?.properties ?? {}) as Record<string, any>;
  return {
    form: { ...(schemaJson?.form ?? {}) },
    schemaRaw: schema ? { ...schema } : null,
    root: Object.entries(properties)
      .sort((a, b) => (a[1]?.["x-index"] ?? 0) - (b[1]?.["x-index"] ?? 0))
      .map(([key, value]) => toNode(key, value)),
  };
};

/* -------------------------------------------------------------- serialise */

const fromNode = (node: Node, index: number): Record<string, any> => {
  // Start from everything we were given, then lay our edits over the top.
  //
  // Every write below is guarded by "did this key exist, or does it now have a
  // value?". Writing unconditionally is what silently rewrote real forms:
  // fields stored without a `type` grew one, and a deliberately empty `title`
  // was deleted because "" is falsy.
  const out: Record<string, any> = { ...node.raw };
  out["x-index"] = index;
  out["x-designable-id"] = node.id;
  if ("x-component" in out || node.component) out["x-component"] = node.component;
  if ("type" in out) out.type = node.type;

  if ("title" in out || node.title) out.title = node.title;

  if (node.required) out.required = true;
  else if (out.required === true) delete out.required;

  if (Object.keys(node.componentProps).length > 0) {
    out["x-component-props"] = node.componentProps;
  }

  // Never touch a rule this designer did not write. `applyRules` would clear
  // the array it found, which on a form built in the classic designer means
  // deleting live validation nobody asked to remove.
  if (!node.foreignRules) applyRules(out, node.rules);

  if (node.enumOptions) {
    // The old designer writes a `children` array on each option; keep the shape
    // so a form can move back and forth between the two designers.
    out.enum = node.enumOptions.map((o) => ({
      children: [],
      label: o.label,
      value: o.value,
    }));
  }

  if (node.children.length > 0) {
    out.properties = Object.fromEntries(
      node.children.map((child, i) => [child.key, fromNode(child, i)])
    );
  } else if (out.properties) {
    // Emptied container: drop the key rather than leave `{}` behind.
    delete out.properties;
  }

  // Columns go back where they came from. `items` carries a `type: "object"`
  // and whatever else the stored table had, so it is merged rather than
  // replaced -- rebuilding it would be the one thing this file exists not to do.
  if (node.itemChildren.length > 0) {
    out.items = {
      type: "object",
      ...(out.items ?? {}),
      properties: Object.fromEntries(
        node.itemChildren.map((child, i) => [child.key, fromNode(child, i)])
      ),
    };
  } else if (out.items?.properties) {
    const rest = { ...out.items };
    delete rest.properties;
    out.items = rest;
  }

  applyDefault(out, node.defaultValue);

  return out;
};

/**
 * Write the default twice: structured, and in the shape Formily reads.
 *
 * The plain `default` is written **only for a fixed value**. A profile default
 * has no value until a particular person opens the form, so writing one would
 * bake whoever last edited the template into the schema -- every requester
 * would then start with that administrator's department. The client resolves
 * those at render time (see `applyDefaults` in the request form).
 */
const applyDefault = (out: Record<string, any>, value: FieldDefault | null) => {
  if (!value) {
    delete out["x-designer-default"];
    if ("default" in out) delete out.default;
    return;
  }
  out["x-designer-default"] = value;
  if (value.source === "fixed") out.default = value.value;
  else if ("default" in out) delete out.default;
};

export const serialise = (doc: Doc): any => {
  const out: Record<string, any> = { form: doc.form };

  // A form with no schema stays that way until something is actually added to
  // it, so round-tripping one does not invent an empty schema object.
  if (!doc.schemaRaw && doc.root.length === 0) return out;

  const schema: Record<string, any> = doc.schemaRaw
    ? { ...doc.schemaRaw }
    : { type: "object" };

  if (doc.root.length > 0 || "properties" in schema) {
    schema.properties = Object.fromEntries(
      doc.root.map((node, i) => [node.key, fromNode(node, i)])
    );
  }

  out.schema = schema;
  return out;
};

/* ---------------------------------------------------------------- editing */

/**
 * The layout palette: the things on a form that are not questions.
 *
 * These are not a nice-to-have. Across the 35 stored forms this client already
 * has **226 grid columns, 85 grids, 59 cards and 38 layouts** — and until now
 * an administrator could not create a single one of them here, which is what
 * kept sending them back to the antd 4 designer for anything but a flat list of
 * fields.
 *
 * `Text` is in this group rather than the field palette because it is the one
 * component that *renders* rather than asks: 24 stored uses, and no way to add
 * a 25th. A heading or a line of guidance is a layout decision.
 */
export const LAYOUT_WIDGETS: WidgetDef[] = [
  { component: "Card", label: "Card", type: "void" },
  { component: "FormGrid", label: "Columns", type: "void" },
  { component: "FormGrid.GridColumn", label: "One column", type: "void" },
  { component: "ArrayTable", label: "Repeating table", type: "array" },
  { component: "Text", label: "Static text", type: "string" },
  // The formatted sibling of `Text`: a heading, a warning and a checklist in
  // one block. `Text` is a single line of plain prose, which is why a form
  // needing instructions has until now had them typed into a field label.
  { component: "Markdown", label: "Guidance", type: "void" },
];

/** A random key, which is what the stored containers use. */
const containerKey = (): string => newId();

/**
 * A fresh container, shaped exactly like the ones already in the database.
 *
 * Every default here was read off a real stored node rather than invented: a
 * card carries its heading in `x-component-props.title` with the client's own
 * 17px/600 header style, a grid carries `maxColumns`/`minColumns`, and a
 * repeating table carries its columns under `items.properties` with the
 * "Add row" button as an ordinary child. A container that merely *looks* right
 * in this designer but does not match what `@formily/antd-v5` renders would be
 * a form that previews correctly and breaks in front of a requester.
 */
export const makeContainer = (
  widget: WidgetDef,
  existingKeys: Set<string>
): Node => {
  const id = newId();
  const component = widget.component;

  if (component === "Card") {
    return baseContainer(id, id, component, {
      title: "Section",
      style: { marginBottom: "16px" },
      styles: { header: { fontSize: "17px", fontWeight: 600 } },
    });
  }

  if (component === "FormGrid") {
    const grid = baseContainer(id, id, component, {
      maxColumns: 2,
      minColumns: 1,
    });
    // A grid with no columns is an invisible box that swallows drops. Two is
    // what `maxColumns` says it is for, and the second can be deleted.
    grid.children = [makeGridColumn(), makeGridColumn()];
    return grid;
  }

  if (component === "FormGrid.GridColumn") return makeGridColumn();

  if (component === "ArrayTable") return makeTable(existingKeys);

  if (component === "Markdown") {
    return {
      id,
      key: id,
      component: "Markdown",
      type: "void",
      title: "",
      required: false,
      // Seeded with something rather than empty: a container that renders
      // nothing is indistinguishable from a broken drop.
      componentProps: { content: "**Before you start**\n\n- " },
      enumOptions: null,
      rules: { ...EMPTY_RULES, validators: [] },
      foreignRules: false,
      children: [],
      itemChildren: [],
      defaultValue: null,
      raw: {
        type: "void",
        "x-component": "Markdown",
        "x-decorator": "FormItem",
        "x-decorator-props": {},
      },
    };
  }

  if (component === "Text") {
    return {
      id,
      key: id,
      component: "Text",
      type: "string",
      title: "",
      required: false,
      componentProps: { content: "Text", mode: "normal",
                        style: { height: "inherit" } },
      enumOptions: null,
      rules: { ...EMPTY_RULES, validators: [] },
      foreignRules: false,
      children: [],
      itemChildren: [],
      defaultValue: null,
      raw: {
        type: "string",
        "x-component": "Text",
        "x-decorator": "FormItem",
        "x-decorator-props": {},
      },
    };
  }

  return baseContainer(id, id, component, {});
};

const baseContainer = (
  id: string,
  key: string,
  component: string,
  componentProps: Record<string, any>
): Node => ({
  id,
  key,
  component,
  type: "void",
  title: "",
  required: false,
  componentProps: { ...componentProps },
  enumOptions: null,
  rules: { ...EMPTY_RULES, validators: [] },
  foreignRules: false,
  children: [],
  itemChildren: [],
  defaultValue: null,
  raw: {
    type: "void",
    "x-component": component,
    "x-validator": [],
    "x-component-props": { ...componentProps },
  },
});

export const makeGridColumn = (): Node => {
  const id = newId();
  return baseContainer(id, id, "FormGrid.GridColumn", {});
};

/**
 * A repeating table with one column and its "Add row" button.
 *
 * The button is a real node the renderer needs, so it is created here and then
 * hidden from the canvas (`isFurniture`): visible, it is one drag away from
 * being dropped somewhere else, after which nobody can add a row and nothing
 * on screen says why.
 */
export const makeTable = (existingKeys: Set<string>): Node => {
  const id = newId();
  let n = 1;
  let key = `table${n}`;
  while (existingKeys.has(key)) key = `table${++n}`;

  const addition = baseContainer(newId(), newId(), "ArrayTable.Addition", {});
  addition.title = "Add row";
  addition.raw = { ...addition.raw, title: "Add row" };

  return {
    id,
    key,
    component: "ArrayTable",
    type: "array",
    title: "Table",
    required: false,
    componentProps: { pagination: { pageSize: 10 }, scroll: { x: "100%" } },
    enumOptions: null,
    rules: { ...EMPTY_RULES, validators: [] },
    foreignRules: false,
    children: [addition],
    itemChildren: [makeColumn("Column 1", existingKeys)],
    defaultValue: null,
    raw: {
      type: "array",
      name: key,
      "x-decorator": "FormItem",
      "x-component": "ArrayTable",
      "x-validator": [],
      "x-decorator-props": {},
      "x-component-props": {
        pagination: { pageSize: 10 },
        scroll: { x: "100%" },
      },
      items: { type: "object" },
    },
  };
};

/** One column, holding one text field. Its inner field is what stores answers. */
export const makeColumn = (title: string, existingKeys: Set<string>): Node => {
  const id = newId();
  let n = 1;
  let key = `column${n}`;
  while (existingKeys.has(key)) key = `column${++n}`;

  const column = baseContainer(id, id, "ArrayTable.Column", {
    title,
    width: 160,
  });
  const inner = makeField(
    { component: "Input", label: title, type: "string" },
    existingKeys
  );
  inner.title = "";
  column.children = [inner];
  return column;
};

/** A fresh field of the given kind, ready to be dropped into a container. */
export const makeField = (widget: WidgetDef, existingKeys: Set<string>): Node => {
  // Field keys are what request data is stored against, so a new one must not
  // collide with a key already in use anywhere in this form.
  let n = 1;
  const base = widgetId(widget).split(".")[0].toLowerCase();
  let key = `${base}${n}`;
  while (existingKeys.has(key)) key = `${base}${++n}`;

  const id = newId();
  return {
    id,
    key,
    component: widget.component,
    type: widget.type,
    title: widget.label,
    required: false,
    // Seeded from the widget's defaults, not left empty: `serialise` writes
    // `x-component-props` from here as soon as anything is edited, so defaults
    // living only in `raw` would vanish the first time someone typed in the
    // inspector.
    componentProps: { ...((widget.extra?.["x-component-props"] as any) ?? {}) },
    enumOptions: HAS_OPTIONS.has(widget.component)
      ? [
          { label: "Option 1", value: "option1" },
          { label: "Option 2", value: "option2" },
        ]
      : null,
    rules: { ...EMPTY_RULES, validators: [] },
    foreignRules: false,
    children: [],
    itemChildren: [],
    defaultValue: null,
    raw: {
      // `type` has to be here, not only on the node: serialising only writes
      // keys the stored node already had, so that forms which omit `type` are
      // not silently given one.
      type: widget.type,
      name: key,
      "x-decorator": "FormItem",
      "x-validator": [],
      "x-decorator-props": { layout: "vertical" },
      ...(widget.extra ?? {}),
    },
  };
};

/** Every key used anywhere in the document, for collision-free naming. */
export const allKeys = (nodes: Node[], into = new Set<string>()): Set<string> => {
  nodes.forEach((n) => {
    into.add(n.key);
    allKeys(n.children, into);
    // A repeating table's columns hold real fields whose keys answers are
    // stored against. Skipping them would let a new field collide with one, and
    // the collision is silent: two fields writing the same key.
    allKeys(n.itemChildren, into);
  });
  return into;
};

/** Depth-first search for a node by id. */
export const find = (nodes: Node[], id: string): Node | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const hit = find(node.children, id) ?? find(node.itemChildren, id);
    if (hit) return hit;
  }
  return null;
};

/** The parent list a node sits in, plus its position — for move and remove. */
export const locate = (
  nodes: Node[],
  id: string,
  parent: Node | null = null
): { siblings: Node[]; index: number; parent: Node | null } | null => {
  const index = nodes.findIndex((n) => n.id === id);
  if (index >= 0) return { siblings: nodes, index, parent };
  for (const node of nodes) {
    const hit =
      locate(node.children, id, node) ?? locate(node.itemChildren, id, node);
    if (hit) return hit;
  }
  return null;
};

export const removeNode = (root: Node[], id: string): boolean => {
  const at = locate(root, id);
  if (!at) return false;
  at.siblings.splice(at.index, 1);
  return true;
};

/** Move a node to sit inside `targetId` at `index`, or reorder among siblings. */
export const moveNode = (
  root: Node[],
  id: string,
  targetParentId: string | null,
  index: number
): boolean => {
  const at = locate(root, id);
  if (!at) return false;
  const node = at.siblings[at.index];

  // Refuse to drop a container inside itself; it would detach the subtree.
  if (targetParentId && (targetParentId === id || find([node], targetParentId))) {
    return false;
  }

  const destination = targetParentId
    ? find(root, targetParentId)?.children
    : root;
  if (!destination) return false;

  at.siblings.splice(at.index, 1);
  const bounded =
    index < 0 || index > destination.length ? destination.length : index;
  destination.splice(bounded, 0, node);
  return true;
};
